/**
 * GPU HEALTH — the one place that knows whether the game's WebGL context is usable, and that rebuilds the
 * scene when it was not.
 *
 * THE BUG THIS EXISTS FOR: a player tabbed away, came back, and the scene was BLANK — the HUD and menubar
 * alive, the canvas empty, for good. Browsers drop a background tab's WebGL context (GPU process resets,
 * sleep/resume, memory pressure, too many contexts); nearly every pixel of a scene is a baked RenderTexture
 * or a `cacheAsTexture` group, and those come back from a context restore EMPTY — Pixi re-uploads textures
 * with an image behind them, but a render target has nothing to re-upload. The old recovery asked for the
 * context back and rendered one frame, which drew a scene of blank textures.
 *
 * So recovery is a REBUILD: `requestSceneRebuild` bumps the GPU EPOCH, App keys the mounted scene on it (and
 * raises the loading curtain), and the scene re-bakes from scratch on the restored context. Module-level
 * texture caches that outlive a scene register an `onGpuReset` hook to drop what the old context held.
 *
 * Three triggers, each a way the screen can go blank:
 *   • the context was RESTORED after a loss (sharedApp `installContextRecovery`);
 *   • frames keep THROWING (a burst of GPU garbage collection after a long absence can poison Pixi's pools);
 *   • the ticker STALLED while the tab is visible (a throw outside every guard stops Pixi's rAF loop).
 * And one dead end: a context that is STILL lost seconds after the tab came back is not coming back — the
 * player is told plainly and offered a reload (`onGpuFatal`), never left with a silent blank page.
 *
 * Rebuilds are rate-limited, so a fault that survives a rebuild cannot spin the game into a rebuild loop.
 */

let lost = false;
let epoch = 0;
let fatal = false;
const epochListeners = new Set<(epoch: number) => void>();
const fatalListeners = new Set<(fatal: boolean) => void>();
const resetHooks = new Set<() => void>();
let lastRebuild = -Infinity;
const recent: number[] = [];
/** At most one rebuild per this many ms, and at most MAX_REBUILDS in REBUILD_WINDOW_MS (then it gives up and
 *  offers the reload — a fault that a rebuild does not fix will not be fixed by the fifth one either). */
const REBUILD_GAP_MS = 3000;
const MAX_REBUILDS = 4;
const REBUILD_WINDOW_MS = 60_000;

/** Is the WebGL context lost right now? Bakes wait while it is (bakeBudget `takeBake`). */
export function gpuLost(): boolean { return lost; }
export function markGpuLost(): void { lost = true; }

/** The current GPU epoch — App keys the mounted scene on it. */
export function gpuEpoch(): number { return epoch; }
export function onGpuEpoch(fn: (epoch: number) => void): () => void { epochListeners.add(fn); return () => { epochListeners.delete(fn); }; }

/**
 * COULD THE GRAPHICS NOT START AT ALL? (sharedApp's `app.init` rejected — no WebGL: hardware acceleration off, a
 * locked-down work PC, a very old GPU.) Different from `fatal` (a context lost mid-session): a reload will not
 * help, the browser has to change. The UI says so instead of a loader that lifts onto an empty scene.
 */
let unavailable = false;
const unavailableListeners = new Set<(on: boolean) => void>();
export function graphicsUnavailable(): boolean { return unavailable; }
export function onGraphicsUnavailable(fn: (on: boolean) => void): () => void { unavailableListeners.add(fn); return () => { unavailableListeners.delete(fn); }; }
export function setGraphicsUnavailable(): void {
  if (unavailable) return;
  unavailable = true;
  for (const fn of unavailableListeners) fn(true);
}

/** Is the context gone for good (the player should reload)? */
export function gpuFatal(): boolean { return fatal; }
export function onGpuFatal(fn: (fatal: boolean) => void): () => void { fatalListeners.add(fn); return () => { fatalListeners.delete(fn); }; }
export function setGpuFatal(on: boolean): void {
  if (fatal === on) return;
  fatal = on;
  for (const fn of fatalListeners) fn(on);
}

/**
 * Register a cache that holds GPU textures beyond a scene's life. Called when the CONTEXT was reset (not on a
 * rebuild for other reasons): drop the references so the next ask re-bakes on the new context. Drop, do not
 * destroy — the outgoing scene may still draw a frame with them before it unmounts.
 */
export function onGpuReset(fn: () => void): () => void { resetHooks.add(fn); return () => { resetHooks.delete(fn); }; }

/**
 * REBUILD THE SCENE: bump the epoch (App remounts the scene under the loading curtain). `contextReset` also
 * runs the cache reset hooks. Returns false when rate-limited or given up (then the reload offer is shown).
 */
export function requestSceneRebuild(reason: string, contextReset = false): boolean {
  const now = performance.now();
  if (contextReset) {
    // a reset context invalidates the caches whether or not a rebuild is allowed right now
    for (const fn of resetHooks) { try { fn(); } catch (e) { console.error("[gpu] cache reset hook threw:", e); } }
  }
  if (now - lastRebuild < REBUILD_GAP_MS) return false;
  while (recent.length && now - recent[0]! > REBUILD_WINDOW_MS) recent.shift();
  if (recent.length >= MAX_REBUILDS) {
    console.error(`[gpu] ${reason} — the scene was rebuilt ${MAX_REBUILDS}× in a minute and it keeps failing; offering a reload`);
    setGpuFatal(true);
    return false;
  }
  lastRebuild = now;
  recent.push(now);
  epoch++;
  console.warn(`[gpu] ${reason} — rebuilding the scene (epoch ${epoch})`);
  for (const fn of epochListeners) fn(epoch);
  return true;
}

/** Context restored: usable again, and everything it held must be rebuilt. */
export function markGpuRestored(): void {
  lost = false;
  setGpuFatal(false);
  requestSceneRebuild("the graphics context was restored", true);
}

/** DEV/TEST: forget the rate limiter (the smoke test triggers several rebuilds in a row). */
export function resetGpuHealthForTest(): void { lastRebuild = -Infinity; recent.length = 0; setGpuFatal(false); }
